import { CompType, PropertyData, propertyHasCustomManualAdjustments } from '@aloft/toolkit-shared';
import {
  Flex,
  FlexProps,
  ProductColor,
  Tooltip,
  Typography,
  TypographyChildren,
  TypographyProps,
  theme,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Badge, SystemStyleObject } from '@aloft/toolkit-sui/chakra';

import { PropsWithChildren } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  selectEffectiveDate,
  selectOrder,
  selectCompType,
} from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectPropertyDataById,
} from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { SubjectPropertyDataState } from '../../../redux/types.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { ResetAdjustmentsButton } from '../ResetAdjustmentsButton.tsx';
import { PropertyGridForm, PropertyGridType } from '../types.ts';
import { getDataSourceDisplayInfo, getSimilarityScore } from '../utils.ts';

type PropertyGridColumnSubHeaderProps = {
  gridType: PropertyGridType;
  columnIndex: number;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
};

const FlexItem = ({
  children,
  backgroundColor = 'surfaceAccent7',
  color = 'textOffset',
  gridColumn = '1 / -1',
  justifyContent = 'space-between',
}: PropsWithChildren<{
  backgroundColor?: ProductColor;
  color?: ProductColor;
  gridColumn?: FlexProps['gridColumn'];
  justifyContent?: FlexProps['justifyContent'];
}>) => {
  const subHeaderFlexProps: FlexProps = {
    height: '32px',
    padding: '4px 8px',
    alignItems: 'center',
    justifyContent,
    gridColumn,
    backgroundColor,
    __dangerDoNotUseSx: {
      color: theme.colors[color],
    },
  };

  return <Flex {...subHeaderFlexProps}>{children}</Flex>;
};

const LabelItem = ({
  children,
  backgroundColor = 'surfaceAccent7',
  color = 'textOffset',
  gridColumn = 'span 1',
}: {
  children: TypographyChildren;
  backgroundColor?: ProductColor;
  color?: ProductColor;
  gridColumn?: SystemStyleObject['gridColumn'];
}) => {
  const typographyProps: TypographyProps = {
    variant: 'body2',
    color,
    __dangerDoNotUseSx: {
      height: '32px',
      paddingLeft: '4px',
      alignContent: 'center',
      backgroundColor: theme.colors[backgroundColor],
      gridColumn,
      whiteSpace: 'nowrap',
    },
  };

  return <Typography {...typographyProps}>{children}</Typography>;
};

export const PropertyGridColumnSubHeader = ({
  gridType,
  columnIndex,
  control,
  getValues,
  setValue,
}: PropertyGridColumnSubHeaderProps) => {
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const { hasArvOption } = useAppSelector(selectOrder);
  const subject = useAppSelector((state) => selectPropertyDataById(state, 'subject')) as
    | SubjectPropertyDataState
    | undefined;
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const isFeatureGrid = gridType === 'FeatureGrid';
  const showAivComps =
    !isFeatureGrid || !hasArvOption || activeTab === 'all' || activeTab === 'aiv';
  const showArvComps =
    !isFeatureGrid || !hasArvOption || activeTab === 'all' || activeTab === 'arv';

  const columnData = useWatch({ control, name: `columns.${columnIndex}` });
  const manualAdjustments = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.manualAdjustments`,
  });

  const { propertyData, propertyId, type } = columnData ?? {};

  const isSubject = propertyId === 'subject';
  const hasCustomManualAdjustments = propertyHasCustomManualAdjustments(manualAdjustments);

  const compType: CompType = useAppSelector((state) => selectCompType(state, propertyId)) ?? 'aiv';
  const compTypeBackgroundColor: ProductColor = `${compType}Light`;

  if (!subject || !effectiveDate || !propertyData) return null;

  const similarityScore = !isSubject
    ? getSimilarityScore(
      subject.subjectData,
      effectiveDate,
      propertyId,
      propertyData as PropertyData,
    )
    : undefined;

  if (type !== 'property') {
    const { subText } = getDataSourceDisplayInfo(type, propertyData);

    return (
      <FlexItem backgroundColor="surfaceAccent5" color="textPrimary" justifyContent="center">
        <Tooltip label={subText} textAlign="center">
          <Typography
            variant="body2"
            textAlign="center"
            truncate
            __dangerDoNotUseSx={{
              whiteSpace: 'nowrap',
            }}
          >
            {subText}
          </Typography>
        </Tooltip>
      </FlexItem>
    );
  }

  if (isSubject) {
    return (
      <>
        <LabelItem gridColumn={!hasArvOption ? '1 / -1' : undefined}>Feature</LabelItem>
        {hasArvOption && (
          <>
            {showAivComps && (
              <LabelItem color="aivText" backgroundColor="aiv">
                As-Is Value
              </LabelItem>
            )}
            {showArvComps && (
              <LabelItem color="arvText" backgroundColor="arv">
                After-Repair Value
              </LabelItem>
            )}
          </>
        )}
      </>
    );
  }

  if (isFeatureGrid) {
    if (!hasArvOption) {
      return (
        <FlexItem backgroundColor="surfaceAccent7">
          <LabelItem>Similarity Score</LabelItem>
          <LabelItem>{similarityScore ?? '-'}</LabelItem>
        </FlexItem>
      );
    }

    return (
      <FlexItem backgroundColor={compTypeBackgroundColor}>
        <LabelItem color="textPrimary" backgroundColor={compTypeBackgroundColor}>
          Similarity Score:&nbsp;
          {similarityScore ?? '-'}
        </LabelItem>
        <Badge
          backgroundColor={compType}
          size="sm"
          textColor={`${compType}Text`}
          borderRadius="4px"
        >
          {compType}
        </Badge>
      </FlexItem>
    );
  }

  const subHeaderItemBackgroundColor: ProductColor = hasArvOption
    ? compTypeBackgroundColor
    : 'surfaceAccent7';
  const subHeaderItemColor: ProductColor = hasArvOption ? 'textPrimary' : 'textOffset';

  return (
    <>
      <LabelItem backgroundColor={subHeaderItemBackgroundColor} color={subHeaderItemColor}>
        Feature
      </LabelItem>
      <FlexItem
        gridColumn="span 1"
        backgroundColor={subHeaderItemBackgroundColor}
        color={subHeaderItemColor}
      >
        <LabelItem color={subHeaderItemColor} backgroundColor={subHeaderItemBackgroundColor}>
          Adjustment
        </LabelItem>
        {hasArvOption && !hasCustomManualAdjustments && (
          <Badge
            backgroundColor={compType}
            size="sm"
            textColor={`${compType}Text`}
            borderRadius="4px"
          >
            {compType}
          </Badge>
        )}
        {hasCustomManualAdjustments && <ResetAdjustmentsButton columnIndex={columnIndex} control={control} getValues={getValues} setValue={setValue} />}
      </FlexItem>
    </>
  );
};
